import React from 'react';
import { Ban, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { deactivateUserOptions, reactivateUserOptions, UserTypeOptions } from '@generated';
import { createQueryDataTable, type PropsOfQueryDataTable, type RowOfQueryDataTable } from '~/shared/data-tables/query/QueryDataTable';
import { Prompt } from '~/shared/components/Prompt';
import { ActiveColumnCell } from '~/shared/data-tables/column-types/ActiveColumn';
import { useMutation } from '~/util/http/useMutation';
import { getSearchQueryKey } from '~/util/http/useSearch';
import { Button } from '~/shared/components/buttons/Button';
import { TableActions } from '~/shared/data-tables/TableActions';

const UsersDataTable = createQueryDataTable('user');
type RowType = RowOfQueryDataTable<typeof UsersDataTable>;
type TableProps = PropsOfQueryDataTable<typeof UsersDataTable>;

function UserTypeCell({ row }: { row: RowType }) {
	const types = row.userTypeSearch;
	if (!types) {
		return null;
	}
	const typeSearch = types.replaceAll('[', '').replaceAll(']', '').split(',');
	const typeSearchLabel = typeSearch.map(x => UserTypeOptions.find(y => y.id.toString() === x)!.label).join(', ');
	return typeSearchLabel;
}

function ActionsCell({ row }: { row: RowType }) {
	const navigate = useNavigate();

	const deactivateUser = useMutation({
		...deactivateUserOptions,
		invalidateQueryKeys: [getSearchQueryKey('user')]
	});

	const reactivateUser = useMutation({
		...reactivateUserOptions,
		invalidateQueryKeys: [getSearchQueryKey('user')]
	});

	return (
		<TableActions>
			{row.active ? (
				<>
					<TableActions.ViewButton aria-label="Edit User" onClick={() => navigate(`edit/${row.id}`)} />
					<Button
						Icon={Ban}
						aria-label="Deactivate User"
						intent="destructive"
						appearance="ghost"
						renderModal={closeModal => (
							<Prompt
								title="Deactivate User"
								text="Are you sure you want to deactivate this user?"
								onAction={() => {
									deactivateUser.mutateAsync({ userId: row.id });
									closeModal();
								}}
								onCancel={closeModal}
							/>
						)}
					/>
				</>
			) : (
				<Button
					Icon={Eye}
					aria-label="Reactivate User"
					appearance="ghost"
					renderModal={closeModal => (
						<Prompt
							title="Reactivate User"
							text="Are you sure you want to reactivate this user?"
							onAction={() => {
								reactivateUser.mutateAsync({ userId: row.id });
								closeModal();
							}}
							onCancel={closeModal}
						/>
					)}
				/>
			)}
		</TableActions>
	);
}

const columnDefinitions = UsersDataTable.columns([
	{
		header: 'Name',
		key: 'name',
		sortable: true,
		width: 'w-[250px]'
	},
	{
		header: 'User Types',
		key: 'userTypeSearch',
		renderCell: row => <UserTypeCell row={row} />
	},
	{
		header: 'Clinician Type',
		key: 'clinicalTypeAbbreviation'
	},
	{
		header: 'Email',
		key: 'emailAddress',
		sortable: true
	},
	{
		header: 'Team',
		key: 'teamName',
		sortable: true
	},
	{
		header: 'Active',
		key: 'active',
		width: 'w-[100px]',
		renderCell: row => <ActiveColumnCell active={row.active} />
	},
	{
		header: 'Actions',
		key: 'actions',
		width: 'w-[100px]',
		renderCell: row => <ActionsCell row={row} />
	}
]);

export function UserTable(props: TableProps) {
	return <UsersDataTable.Table columns={columnDefinitions} emptyMessage="No users found." getMuteRowOpacity={row => !row.active} {...props} />;
}
